import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../utils/api';

const DashboardContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const WelcomeSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
`;

const WelcomeTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 0.5rem;
`;

const WelcomeText = styled.p`
  font-size: 1rem;
  color: #6c757d;
`;

const QuickLinks = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
`;

const QuickLinksTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1.5rem;
`;

const LinkGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
`;

const LinkCard = styled.div`
  padding: 1.5rem 1rem;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #000000;
    background: #f8f9fa;
  }
`;

const LinkIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.75rem;
`;

const LinkLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #000000;
`;

const InfoSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const InfoTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid #dee2e6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-size: 0.9375rem;
  color: #6c757d;
  font-weight: 600;
`;

const InfoValue = styled.span`
  font-size: 0.9375rem;
  color: #000000;
  font-weight: 500;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const StatIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.75rem;
`;

const StatLabel = styled.div`
  font-size: 0.8125rem;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const RecentSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1.5rem;
`;

const NoticeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const NoticeItem = styled.div`
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #000000;
    background: #f8f9fa;
  }
`;

const NoticeTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #000000;
  margin-bottom: 0.25rem;
`;

const NoticeDate = styled.div`
  font-size: 0.8125rem;
  color: #6c757d;
`;

const ScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ScheduleItem = styled.div`
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #000000;
    background: #f8f9fa;
  }
`;

const ScheduleDate = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #000000;
  margin-bottom: 0.25rem;
`;

const ScheduleInfo = styled.div`
  font-size: 0.8125rem;
  color: #6c757d;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  font-size: 0.9375rem;
`;

interface DashboardStats {
  feePaid: number;
  feeUnpaid: number;
  feeTotal: number;
  hasUniform: boolean;
}

interface Notice {
  id: number;
  title: string;
  created_at: string;
}

interface Schedule {
  schedule_no: number;
  schedule_date: string;
  schedule_time: string;
  location: string;
}

const MemberDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    feePaid: 0,
    feeUnpaid: 0,
    feeTotal: 0,
    hasUniform: false
  });
  const [recentNotices, setRecentNotices] = useState<Notice[]>([]);
  const [upcomingSchedules, setUpcomingSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();

  // 대시보드 데이터 조회
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // 회비 통계
        const feeResponse = await apiClient.get(`/fees/my-fees?year=${currentYear}`);
        if (feeResponse.data.success) {
          const payments = feeResponse.data.data;
          const paidMonths = payments.length;
          const unpaidMonths = 12 - paidMonths;
          const total = payments.reduce((sum: number, p: any) => sum + (parseInt(p.amount) || 0), 0);
          
          setStats(prev => ({
            ...prev,
            feePaid: paidMonths,
            feeUnpaid: unpaidMonths,
            feeTotal: total
          }));
        }

        // 유니폼 정보
        const uniformResponse = await apiClient.get('/uniforms/my-uniform');
        if (uniformResponse.data.success) {
          const hasUniform = uniformResponse.data.data.length > 0 && 
                            uniformResponse.data.data.some((u: any) => u.number);
          setStats(prev => ({ ...prev, hasUniform }));
        }

        // 최근 공지사항 (최근 5개)
        const noticeResponse = await apiClient.get('/notices/public');
        if (noticeResponse.data.success) {
          const notices = noticeResponse.data.data.slice(0, 5).map((n: any) => ({
            id: n.id || n.notice_id,
            title: n.title,
            created_at: n.created_at || n.createdAt
          }));
          setRecentNotices(notices);
        }

        // 다가오는 일정 (최근 5개)
        const scheduleResponse = await apiClient.get('/schedules');
        if (scheduleResponse.data.success) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const upcoming = scheduleResponse.data.data
            .filter((s: any) => {
              const scheduleDate = new Date(s.schedule_date);
              scheduleDate.setHours(0, 0, 0, 0);
              return scheduleDate >= today;
            })
            .sort((a: any, b: any) => {
              const dateA = new Date(a.schedule_date);
              const dateB = new Date(b.schedule_date);
              return dateA.getTime() - dateB.getTime();
            })
            .slice(0, 5)
            .map((s: any) => ({
              schedule_no: s.schedule_no,
              schedule_date: s.schedule_date,
              schedule_time: s.schedule_time,
              location: s.location
            }));
          
          setUpcomingSchedules(upcoming);
        }
      } catch (error) {
        console.error('대시보드 데이터 조회 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleNoticeClick = (noticeId: number) => {
    navigate(`/member/notices`);
  };

  const handleScheduleClick = () => {
    navigate('/member/schedule');
  };

  if (loading) {
    return (
      <DashboardLayout title="마이페이지">
        <DashboardContainer>
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>로딩 중...</div>
        </DashboardContainer>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout title="마이페이지">
      <DashboardContainer>
        <WelcomeSection>
          <WelcomeTitle>안녕하세요, {user?.name}님 👋</WelcomeTitle>
          <WelcomeText>H_B 배구동호회 회원 페이지입니다.</WelcomeText>
        </WelcomeSection>
        
        {/* 통계 카드 */}
        <StatsGrid>
          <StatCard onClick={() => navigate('/member/fees')} style={{ cursor: 'pointer' }}>
            <StatIcon>💰</StatIcon>
            <StatLabel>납부 개월</StatLabel>
            <StatValue>{stats.feePaid}개월</StatValue>
          </StatCard>
          <StatCard onClick={() => navigate('/member/fees')} style={{ cursor: 'pointer' }}>
            <StatIcon>⚠️</StatIcon>
            <StatLabel>미납 개월</StatLabel>
            <StatValue>{stats.feeUnpaid}개월</StatValue>
          </StatCard>
          <StatCard onClick={() => navigate('/member/fees')} style={{ cursor: 'pointer' }}>
            <StatIcon>💵</StatIcon>
            <StatLabel>총 납부액</StatLabel>
            <StatValue>{stats.feeTotal.toLocaleString()}원</StatValue>
          </StatCard>
          <StatCard onClick={() => navigate('/member/uniforms')} style={{ cursor: 'pointer' }}>
            <StatIcon>👕</StatIcon>
            <StatLabel>유니폼</StatLabel>
            <StatValue>{stats.hasUniform ? '등록됨' : '미등록'}</StatValue>
          </StatCard>
        </StatsGrid>

        {/* 최근 공지사항 & 다가오는 일정 */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '2rem', 
          marginBottom: '2rem' 
        }}>
          <RecentSection>
            <SectionTitle>최근 공지사항</SectionTitle>
            {recentNotices.length > 0 ? (
              <NoticeList>
                {recentNotices.map(notice => (
                  <NoticeItem key={notice.id} onClick={() => handleNoticeClick(notice.id)}>
                    <NoticeTitle>{notice.title}</NoticeTitle>
                    <NoticeDate>{notice.created_at}</NoticeDate>
                  </NoticeItem>
                ))}
              </NoticeList>
            ) : (
              <EmptyMessage>공지사항이 없습니다</EmptyMessage>
            )}
          </RecentSection>

          <RecentSection>
            <SectionTitle>다가오는 일정</SectionTitle>
            {upcomingSchedules.length > 0 ? (
              <ScheduleList>
                {upcomingSchedules.map(schedule => (
                  <ScheduleItem key={schedule.schedule_no} onClick={handleScheduleClick}>
                    <ScheduleDate>{schedule.schedule_date} {schedule.schedule_time}</ScheduleDate>
                    <ScheduleInfo>{schedule.location}</ScheduleInfo>
                  </ScheduleItem>
                ))}
              </ScheduleList>
            ) : (
              <EmptyMessage>다가오는 일정이 없습니다</EmptyMessage>
            )}
          </RecentSection>
        </div>
        
        {/* 빠른 메뉴 */}
        <QuickLinks>
          <QuickLinksTitle>빠른 메뉴</QuickLinksTitle>
          <LinkGrid>
            <LinkCard onClick={() => navigate('/member/schedule')}>
              <LinkIcon>🏐</LinkIcon>
              <LinkLabel>운동 일정</LinkLabel>
            </LinkCard>
            <LinkCard onClick={() => navigate('/member/fees')}>
              <LinkIcon>💰</LinkIcon>
              <LinkLabel>회비</LinkLabel>
            </LinkCard>
            <LinkCard onClick={() => navigate('/member/uniforms')}>
              <LinkIcon>👕</LinkIcon>
              <LinkLabel>유니폼</LinkLabel>
            </LinkCard>
            <LinkCard onClick={() => navigate('/member/competitions')}>
              <LinkIcon>🏆</LinkIcon>
              <LinkLabel>대회</LinkLabel>
            </LinkCard>
            <LinkCard onClick={() => navigate('/member/guest-apply')}>
              <LinkIcon>👋</LinkIcon>
              <LinkLabel>게스트 신청</LinkLabel>
            </LinkCard>
          </LinkGrid>
        </QuickLinks>
        
        {/* 내 정보 */}
        <InfoSection>
          <InfoTitle>내 정보</InfoTitle>
          <InfoRow>
            <InfoLabel>이름</InfoLabel>
            <InfoValue>{user?.name}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>아이디</InfoLabel>
            <InfoValue>{user?.user_id}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>회원 상태</InfoLabel>
            <InfoValue>{user?.status === 'A' ? '활동' : user?.status === 'R' ? '신청' : '휴면'}</InfoValue>
          </InfoRow>
        </InfoSection>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default MemberDashboard;

